import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ResponseError, responseErrorsResolver } from '@ui/src';
import { useAppDispatch } from '@store';
import {
  ApplicationDocument,
  ApplicationDocumentStatus,
} from '../../../../../hoc/new-application/types';
import {
  useAddCofApplicationDocumentsMutation,
  useAddCorporateApplicationDocumentsMutation,
  useAddIndividualApplicationDocumentsMutation,
  useBatchDeleteCOFApplicationDocumentsMutation,
  useBatchDeleteCorporateApplicationDocumentsMutation,
  useBatchDeleteIndividualApplicationDocumentsMutation,
  useChangeCOFApplicationDocumentCategoryMutation,
  useChangeCOFApplicationDocumentStatusMutation,
  useChangeCorporateApplicationDocumentCategoryMutation,
  useChangeCorporateApplicationDocumentStatusMutation,
  useChangeIndividualApplicationDocumentCategoryMutation,
  useChangeIndividualApplicationDocumentStatusMutation,
  useLazyGetCofApplicationDocumentFileQuery,
  useLazyGetCorporateApplicationDocumentFileQuery,
  useLazyGetIndividualApplicationDocumentFileQuery,
  useRenameCofDocumentMutation,
  useRenameCorporateDocumentMutation,
  useRenameIndividualDocumentMutation,
} from '../../../../../hoc/new-application/store/services';
import {
  deleteIsSuccess,
  onDocumentMove,
  onSelectAllGroup,
} from '../../../../../hoc/new-application/individual/store/slices';
import {
  onDocumentMove as onCofDocumentMove,
  deleteIsSuccess as cofDeleteIsSuccess,
  onSelectAllGroup as onSelectCofAllGroup,
} from '../../../../../hoc/new-application/main-representative/store/slices';
import {
  onDocumentMove as onCorporateDocumentMove,
  deleteIsSuccess as corporateDeleteIsSuccess,
  onSelectAllGroup as onSelectCorporateAllGroup,
} from '../../../../../hoc/new-application/corporate/store/slices';
import { useCheckDocumentStringNameMutation } from '../../../../../../../../store/service';
import { useCustomersUploadMutation } from '../../../../../store/documents/services';
import { UploadResponse } from '../../../../../store/documents/types';

const useDocuments = ({
  isCof,
  isCorporate,
  applicationId,
  documents,
  deleteSuccess,
}: {
  isCof?: boolean;
  isCorporate?: boolean;
  applicationId?: string;
  documents?: ApplicationDocument[];
  deleteSuccess?: boolean;
}) => {
  const dropZoneInputRef = useRef<HTMLInputElement | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const [menuVisible, setMenuVisible] = useState(false);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);

  const [selectedGroup, setSelectedGroup] = useState<string | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<
    ApplicationDocumentStatus | undefined
  >();
  const [uploadedFiles, setUploadedFiles] = useState<
    { name: string; id: string }[]
  >([]);
  const [uploadedFilesErrors, setUploadedFilesError] = useState<string[]>([]);
  const [expirationDate, setExpirationDate] = useState<Date | undefined>(
    undefined,
  );
  const [renamePopupVisible, setRenamePopupVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<
    ApplicationDocument | undefined
  >(undefined);
  const [documentName, setDocumentName] = useState('');
  const [nameError, setNameError] = useState('');
  const [documentGroupError, setDocumentGroupError] = useState('');
  const [attachedDocumentsErrors, setAttachedDocumentsErrors] = useState<
    Record<string, string> | undefined
  >(undefined);
  const [attachedFiles, setAttachedFiles] = useState<string[]>([]);
  const [uploadedDocumentsErrors, setUploadedDocumentsErrors] = useState<
    { name: string; error: string }[]
  >([]);

  const {
    setError,
    formState: { errors: renameErrors },
  } = useForm();

  const dispatch = useAppDispatch();

  const [
    getIndividualApplicationDocumentFile,
    { isLoading, isSuccess, isError },
  ] = useLazyGetIndividualApplicationDocumentFileQuery();
  const [
    changeIndividualDocumentStatus,
    { isLoading: statusChangeInProgress, isSuccess: statusChangeIsSuccess },
  ] = useChangeIndividualApplicationDocumentStatusMutation();
  const [changeIndividualDocumentCategory, { isLoading: moveInProgress }] =
    useChangeIndividualApplicationDocumentCategoryMutation();
  const [deleteIndividualAppicationDocuments, { isLoading: deleteInProgress }] =
    useBatchDeleteIndividualApplicationDocumentsMutation();
  const [rename, { isLoading: renameInProgress, isSuccess: renameIsSuccess }] =
    useRenameIndividualDocumentMutation();

  const [
    getCofApplicationDocumentFile,
    {
      isLoading: cofDocumentIsLoading,
      isSuccess: cofDocumentIsSuccess,
      isError: cofDocumentError,
    },
  ] = useLazyGetCofApplicationDocumentFileQuery();
  const [
    changeCofDocumentStatus,
    {
      isLoading: cofStatusChangeInProgress,
      isSuccess: cofStatusChangeIsSuccess,
    },
  ] = useChangeCOFApplicationDocumentStatusMutation();
  const [changeCOFDocumentCategory, { isLoading: cofMoveInProgress }] =
    useChangeCOFApplicationDocumentCategoryMutation();
  const [deleteCofAppicationDocuments, { isLoading: cofDeleteInProgress }] =
    useBatchDeleteCOFApplicationDocumentsMutation();
  const [
    cofRename,
    { isLoading: cofRenameInProgress, isSuccess: cofRenameIsSuccess },
  ] = useRenameCofDocumentMutation();
  const [
    getCorporateApplicationDocumentFile,
    {
      isLoading: corporateDocumentsIsLoading,
      isSuccess: corporateDocumentsIsSuccess,
      isError: corporateDocumentsIsError,
    },
  ] = useLazyGetCorporateApplicationDocumentFileQuery();
  const [
    changeCorporateDocumentStatus,
    {
      isLoading: corporateStatusChangeInProgress,
      isSuccess: corporateStatusChangeIsSuccess,
    },
  ] = useChangeCorporateApplicationDocumentStatusMutation();
  const [
    changeCorporateDocumentCategory,
    { isLoading: corporateMoveInProgress },
  ] = useChangeCorporateApplicationDocumentCategoryMutation();
  const [
    deleteCorporateAppicationDocuments,
    { isLoading: corporateDeleteInProgress },
  ] = useBatchDeleteCorporateApplicationDocumentsMutation();
  const [
    corporateRename,
    {
      isLoading: corporateRenameInProgress,
      isSuccess: corporateRenameIsSuccess,
    },
  ] = useRenameCorporateDocumentMutation();
  const [checkDocumentName, { isLoading: checkDocumentNameInProgress }] =
    useCheckDocumentStringNameMutation();
  const [upload, { isLoading: fileUploadInProgress }] =
    useCustomersUploadMutation();
  const [
    addIndividualApplicationDocument,
    { isLoading: documentSubmitInProgress },
  ] = useAddIndividualApplicationDocumentsMutation();
  const [
    addCofApplicationDocument,
    { isLoading: cofDocumentSubmitInProgress },
  ] = useAddCofApplicationDocumentsMutation();
  const [
    addCorporateApplicationDocument,
    { isLoading: corporateDocumentSubmitInProgress },
  ] = useAddCorporateApplicationDocumentsMutation();

  const onRename = async () => {
    if (documentName?.length < 3) {
      setNameError('name must be longer than or equal to 3 characters');
    } else {
      setNameError('');

      let response;

      if (isCof) {
        response = (await cofRename({
          id: applicationId as string,
          documentId: selectedDocument?.id as string,
          body: {
            name: documentName,
          },
        })) as { error?: ResponseError };
      } else if (isCorporate) {
        response = (await corporateRename({
          id: applicationId as string,
          documentId: selectedDocument?.id as string,
          body: {
            name: documentName,
          },
        })) as { error?: ResponseError };
      } else {
        response = (await rename({
          id: applicationId as string,
          documentId: selectedDocument?.id as string,
          body: {
            name: documentName,
          },
        })) as { error?: ResponseError };
      }

      if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
        });
      }
    }
  };

  const changeDocumentStatusAction = (status: ApplicationDocumentStatus) => {
    const selectedDocuments = documents?.filter(
      (item) =>
        item.isSelected && item.status !== ApplicationDocumentStatus.EXPIRED,
    );

    if (selectedDocuments && selectedDocuments.length) {
      if (isCof) {
        changeCofDocumentStatus({
          id: applicationId as string,
          body: {
            ids: selectedDocuments?.map((item) => item.id),
            status,
          },
        });
      } else if (isCorporate) {
        changeCorporateDocumentStatus({
          id: applicationId as string,
          body: {
            ids: selectedDocuments?.map((item) => item.id),
            status,
          },
        });
      } else {
        changeIndividualDocumentStatus({
          id: applicationId as string,
          body: {
            ids: selectedDocuments?.map((item) => item.id),
            status,
          },
        });
      }
    }
  };

  const changeDocumentCategoryAction = async (documentCategoryId: string) => {
    const selectedDocuments = documents?.filter(
      (item) =>
        item.isSelected && item.documentCategoryId !== documentCategoryId,
    );
    if (selectedDocuments && selectedDocuments.length) {
      let response;

      if (isCof) {
        response = await changeCOFDocumentCategory({
          id: applicationId as string,
          body: {
            ids: selectedDocuments?.map((item) => item.id),
            documentCategoryId,
          },
        });
      } else if (isCorporate) {
        response = await changeCorporateDocumentCategory({
          id: applicationId as string,
          body: {
            ids: selectedDocuments?.map((item) => item.id),
            documentCategoryId,
          },
        });
      } else {
        response = await changeIndividualDocumentCategory({
          id: applicationId as string,
          body: {
            ids: selectedDocuments?.map((item) => item.id),
            documentCategoryId,
          },
        });
      }
      if (response) {
        if (isCof) {
          dispatch(onCofDocumentMove(documentCategoryId));
        } else if (isCorporate) {
          dispatch(onCorporateDocumentMove(documentCategoryId));
        } else {
          dispatch(onDocumentMove(documentCategoryId));
        }
      }
    }
  };

  const onDeleteDocuments = async () => {
    const selectedDocuments = documents?.filter((item) => item.isSelected);

    if (selectedDocuments && selectedDocuments.length) {
      let response;

      if (isCof) {
        response = await deleteCofAppicationDocuments({
          id: applicationId as string,
          documentId: selectedDocuments.map((item) => item.id),
        });
      } else if (isCorporate) {
        response = await deleteCorporateAppicationDocuments({
          id: applicationId as string,
          documentId: selectedDocuments.map((item) => item.id),
        });
      } else {
        response = await deleteIndividualAppicationDocuments({
          id: applicationId as string,
          documentId: selectedDocuments.map((item) => item.id),
        });
      }

      if (response) {
        setDeletePopupVisible(false);

        const nextDocuments = documents?.filter((document) =>
          selectedDocuments.every((item) => document.id !== item.id),
        );

        if (isCof) {
          dispatch(cofDeleteIsSuccess(nextDocuments as ApplicationDocument[]));
        } else if (isCorporate) {
          dispatch(
            corporateDeleteIsSuccess(nextDocuments as ApplicationDocument[]),
          );
        } else {
          dispatch(deleteIsSuccess(nextDocuments as ApplicationDocument[]));
        }
      }
    }
  };

  const getFiles = async () => {
    const selectedDocuments = documents?.filter((item) => item.isSelected);

    if (selectedDocuments && selectedDocuments.length) {
      let downloadedFiles;

      if (isCof) {
        downloadedFiles = await Promise.all(
          selectedDocuments.map(async (item) => {
            const response = await getCofApplicationDocumentFile({
              id: applicationId as string,
              documentId: item.id,
            });

            return response.data;
          }),
        );
      } else if (isCorporate) {
        downloadedFiles = await Promise.all(
          selectedDocuments.map(async (item) => {
            const response = await getCorporateApplicationDocumentFile({
              id: applicationId as string,
              documentId: item.id,
            });

            return response.data;
          }),
        );
      } else {
        downloadedFiles = await Promise.all(
          selectedDocuments.map(async (item) => {
            const response = await getIndividualApplicationDocumentFile({
              id: applicationId as string,
              documentId: item.id,
            });

            return response.data;
          }),
        );
      }

      if (downloadedFiles) {
        downloadedFiles.forEach((file, index) => {
          const hiddenElement = document.createElement('a');
          const url = window.URL || window.webkitURL;

          // @ts-ignore
          const blob = url.createObjectURL(file);
          hiddenElement.href = blob;
          hiddenElement.target = '_blank';
          hiddenElement.download = selectedDocuments[index].name;
          hiddenElement.click();
        });
      }
    }
  };

  const onGroupSelect = (selected: boolean) => {
    if (isCof) {
      dispatch(onSelectCofAllGroup(selected));
    } else if (isCorporate) {
      dispatch(onSelectCorporateAllGroup(selected));
    } else {
      dispatch(onSelectAllGroup(selected));
    }
  };

  const handleCloseCreateDocumentSidepanel = () => {
    setAttachedDocumentsErrors(undefined);
    setUploadedDocumentsErrors([]);
    setExpirationDate(undefined);

    if (uploadInputRef?.current) {
      uploadInputRef.current.value = '';
    }

    if (dropZoneInputRef?.current) {
      dropZoneInputRef.current.value = '';
    }

    setUploadedFiles([]);
    setSelectedGroup(undefined);
    setPanelIsOpen(false);
    setUploadedFilesError([]);
    setAttachedDocumentsErrors(undefined);
    setAttachedFiles([]);
  };

  const onSubmitDocuments = async () => {
    if (!selectedGroup) {
      setDocumentGroupError('Group is required');
    } else {
      setDocumentGroupError('');
      setAttachedDocumentsErrors(undefined);

      const nextAttachedDocumentsErrors = { ...attachedDocumentsErrors };

      const nextAttachedFiles = ([] as string[]).concat(attachedFiles);

      const responses = await Promise.all(
        uploadedFiles
          ?.filter((item) => nextAttachedFiles?.every((id) => item.id !== id))
          .map(async (item) => {
            const body: {
              uploadId: string;
              name: string;
              expirationDate?: string;
              documentCategoryId?: string;
            } = {
              uploadId: item.id,
              name: item.name,
            };

            if (selectedGroup) {
              body.documentCategoryId = selectedGroup;
            }

            if (expirationDate) {
              body.expirationDate = expirationDate.toISOString();
            }

            let response;

            if (isCof) {
              response = (await addCofApplicationDocument({
                id: applicationId as string,
                body,
              })) as {
                error?: {
                  data: {
                    message?: {
                      constraints: Record<string, string>;
                    }[];
                  };
                };
              };
            } else if (isCorporate) {
              response = (await addCorporateApplicationDocument({
                id: applicationId as string,
                body,
              })) as {
                error?: {
                  data: {
                    message?: {
                      constraints: Record<string, string>;
                    }[];
                  };
                };
              };
            } else {
              response = (await addIndividualApplicationDocument({
                id: applicationId as string,
                body,
              })) as {
                error?: {
                  data: {
                    message?: {
                      constraints: Record<string, string>;
                    }[];
                  };
                };
              };
            }

            if (response.error && response?.error?.data?.message?.length) {
              const errorItem = response?.error?.data?.message[0].constraints;

              if (errorItem) {
                const [documentValue] = Object.values(errorItem);

                nextAttachedDocumentsErrors[item.id] = documentValue;
              }
            } else {
              nextAttachedFiles.push(item.id);
            }

            return response;
          }),
      );

      setAttachedFiles(nextAttachedFiles);
      setAttachedDocumentsErrors(nextAttachedDocumentsErrors);

      if (responses && responses.length) {
        if (!responses?.find((item) => item.error)) {
          handleCloseCreateDocumentSidepanel();
        }
      }
    }
  };

  const onUpload = async (files: FileList) => {
    setUploadedFilesError([]);
    setUploadedDocumentsErrors([]);

    const nextUploadedFilesErrors: string[] = [];
    const nextUploadedDocumentsErrors: { name: string; error: string }[] = [];

    const responseFiles = await Promise.all(
      Object.keys(files).map(async (file) => {
        const checkNameResponse = (await checkDocumentName({
          value: files[file as any].name,
        })) as {
          error?: {
            data: {
              message?: {
                constraints: Record<string, string>;
              }[];
            };
          };
        };

        let nextFile;

        if (!checkNameResponse?.error) {
          const response = (await upload(files[file as any])) as unknown as {
            data?: UploadResponse[];
            error?: {
              originalStatus: number;
              data?: {
                message?: string;
              };
            };
            message?: string;
          };

          if (response && response.data) {
            nextFile = { ...response.data[0] };

            let fileName = files[file as any]?.name;

            if (
              response.data[0] &&
              response.data[0]?.mimeType?.includes('image')
            ) {
              fileName = `${files[file as any]?.name.slice(0, files[file as any]?.name.lastIndexOf('.'))}.jpg`;
            }

            nextFile.name = fileName;
          } else if (response.error) {
            if (response.error.originalStatus === 413) {
              nextUploadedFilesErrors.push(
                `${files[file as any].name} size too large`,
              );
            } else {
              // eslint-disable-next-line no-lonely-if
              if (response?.error?.data?.message) {
                nextUploadedFilesErrors.push(response?.error?.data?.message);
              }
            }
          }
        } else if (
          checkNameResponse?.error &&
          checkNameResponse?.error?.data?.message
        ) {
          const errorItem =
            checkNameResponse?.error?.data?.message[0]?.constraints;

          if (errorItem) {
            nextUploadedDocumentsErrors.push({
              name: files[file as any].name,
              error: Object.values(errorItem)[0],
            });
          }
        }

        return nextFile;
      }),
    );

    setUploadedFilesError(nextUploadedFilesErrors);
    setUploadedDocumentsErrors(nextUploadedDocumentsErrors);

    if (responseFiles && responseFiles.length) {
      const nextUploadedFiles = ([] as { name: string; id: string }[]).concat(
        uploadedFiles,
      );

      responseFiles.forEach((item) => {
        if (item && item.name && item.id) {
          nextUploadedFiles.push({
            name: item?.name,
            id: item?.id,
          });
        }
      });

      setUploadedFiles(nextUploadedFiles);
    }

    if (uploadInputRef?.current) {
      uploadInputRef.current.value = '';
    }
    if (dropZoneInputRef?.current) {
      dropZoneInputRef.current.value = '';
    }
  };

  const onDelete = (id: string) => {
    if (uploadInputRef?.current) {
      uploadInputRef.current.value = '';
    }

    if (dropZoneInputRef?.current) {
      dropZoneInputRef.current.value = '';
    }

    setUploadedFiles(uploadedFiles.filter((item) => item.id !== id));

    const nextAttachedDocumentsErrors = { ...attachedDocumentsErrors };

    if (nextAttachedDocumentsErrors[id]) {
      delete nextAttachedDocumentsErrors[id];

      setAttachedDocumentsErrors(nextAttachedDocumentsErrors);
    }
  };

  useEffect(() => {
    if (renameIsSuccess || cofRenameIsSuccess || corporateRenameIsSuccess) {
      setRenamePopupVisible(false);
      setDocumentName('');
      setSelectedDocument(undefined);
      setNameError('');
    }
  }, [renameIsSuccess, cofRenameIsSuccess, corporateRenameIsSuccess]);

  useEffect(() => {
    if (deleteSuccess) {
      setDeletePopupVisible(false);
    }
  }, [deleteSuccess]);

  useEffect(() => {
    if (
      isSuccess ||
      isError ||
      statusChangeIsSuccess ||
      cofDocumentIsSuccess ||
      cofDocumentError ||
      cofStatusChangeIsSuccess ||
      corporateDocumentsIsSuccess ||
      corporateDocumentsIsError ||
      corporateStatusChangeIsSuccess
    ) {
      if (isCof) {
        dispatch(onSelectCofAllGroup(false));
      } else if (isCorporate) {
        dispatch(onSelectCorporateAllGroup(false));
      } else {
        dispatch(onSelectAllGroup(false));
      }

      if (
        statusChangeIsSuccess ||
        cofStatusChangeIsSuccess ||
        corporateStatusChangeIsSuccess
      ) {
        setSelectedStatus(undefined);
      }
    }
  }, [
    isSuccess,
    isError,
    statusChangeIsSuccess,
    cofDocumentIsSuccess,
    cofDocumentError,
    cofStatusChangeIsSuccess,
    isCof,
    isCorporate,
    corporateDocumentsIsSuccess,
    corporateDocumentsIsError,
    corporateStatusChangeIsSuccess,
  ]);

  useEffect(() => {
    if (selectedDocument && !documentName) {
      setDocumentName(selectedDocument.name);
    }
  }, [selectedDocument]);

  useEffect(() => {
    if (documentGroupError && selectedGroup) {
      setDocumentGroupError('');
    }
  }, [documentGroupError, selectedGroup]);

  return {
    setPanelIsOpen,
    setMenuVisible,
    menuVisible,
    moveInProgress:
      moveInProgress || cofMoveInProgress || corporateMoveInProgress,
    changeDocumentCategoryAction,
    statusChangeInProgress:
      statusChangeInProgress ||
      cofStatusChangeInProgress ||
      corporateStatusChangeInProgress,
    selectedStatus,
    setSelectedStatus,
    changeDocumentStatusAction,
    isLoading: isLoading || cofDocumentIsLoading || corporateDocumentsIsLoading,
    getFiles,
    setDeletePopupVisible,
    onGroupSelect,
    setRenamePopupVisible,
    setSelectedDocument,
    onDeleteDocuments,
    deleteInProgress:
      deleteInProgress || cofDeleteInProgress || corporateDeleteInProgress,
    deletePopupVisible,
    setDocumentName,
    setNameError,
    onRename,
    renameInProgress:
      renameInProgress || cofRenameInProgress || corporateRenameInProgress,
    renamePopupVisible,
    selectedDocument,
    documentName,
    nameError,
    renameErrors,
    panelIsOpen,
    handleCloseCreateDocumentSidepanel,
    documentSubmitInProgress:
      documentSubmitInProgress ||
      cofDocumentSubmitInProgress ||
      corporateDocumentSubmitInProgress,
    onSubmitDocuments,
    uploadInputRef,
    dropZoneInputRef,
    uploadedFiles,
    setUploadedFiles,
    uploadedFilesErrors,
    selectedGroup,
    onUpload,
    setSelectedGroup,
    checkDocumentNameInProgress,
    fileUploadInProgress,
    onDelete,
    setExpirationDate,
    expirationDate,
    documentGroupError,
    attachedDocumentsErrors,
    attachedFiles,
    uploadedDocumentsErrors,
  };
};

export default useDocuments;
